import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  Card,
  Container,
  Fade,
  alpha,
} from "@mui/material";
import { sheetBackground } from "@/theme/surfaces";
import { AssignmentOutlined as QuestionnaireIcon } from "@mui/icons-material";
import { RequiredAction } from "../../hooks/useCerberus";
import { useBranding } from "../../branding/BrandingProvider";

interface WelcomeSurveyDialogProps {
  open: boolean;
  action: RequiredAction;
  onComplete: (completionData: Record<string, unknown>) => Promise<void>;
  onGoToQuestionnaire?: () => void;
  isNavigating?: boolean;
}

/**
 * The TeleHeadache welcome: a greeting and a hand-off to the headache questionnaire.
 *
 * It used to open by asking what the patient wanted — treatment, a refill (with a medication and
 * dose picker), or a video visit. That question now lives at the END of the questionnaire
 * (modules/headache-questionnaire/QuestionnaireNextStep.tsx), where the answer becomes a real
 * medication request the reviewing provider sees beside the answers, and where it is asked again
 * every time the questionnaire is retaken rather than once at onboarding. Asking it here as well
 * would have a new patient answer the same question twice, minutes apart.
 *
 * Patients who answered the old question keep their answer: it is still in
 * UserActionCompletions.CompletionData, and the API still reads `selectedChoice` from it for
 * submissions made before the end-of-questionnaire step existed.
 */
const WelcomeSurveyDialog: React.FC<WelcomeSurveyDialogProps> = ({
  open,
  onComplete,
  onGoToQuestionnaire,
  isNavigating = false,
}) => {
  const brand = useBranding();
  const [completing, setCompleting] = useState(false);

  const handleGoToQuestionnaire = async () => {
    setCompleting(true);

    try {
      await onComplete({ completedAt: new Date().toISOString() });
      onGoToQuestionnaire?.();
    } catch (error) {
      console.error("Failed to complete welcome survey:", error);
    } finally {
      setCompleting(false);
    }
  };

  const renderHeroSection = () => (
    <Box
      sx={{
        background: (theme) => theme.palette.brand.gradient.hero,
        color: "brand.onHero",
        py: { xs: 3, md: 4 },
        px: 4,
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: "absolute",
          top: -50,
          right: -50,
          width: 200,
          height: 200,
          borderRadius: "50%",
          background: (theme) => alpha(theme.palette.brand.onHero, 0.1),
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -30,
          left: -30,
          width: 150,
          height: 150,
          borderRadius: "50%",
          background: (theme) => alpha(theme.palette.brand.onHero, 0.05),
        }}
      />

      {/* Logo */}
      <Box sx={{ mb: 3 }}>
        <img
          src={brand.logoWhite}
          alt={`${brand.displayName} logo`}
          style={{
            maxHeight: "80px",
            maxWidth: "300px",
            objectFit: "contain",
          }}
        />
      </Box>

      {/* Welcome text */}
      <Typography
        variant="h3"
        component="h1"
        sx={{
          fontWeight: 700,
          mb: 2,
          fontSize: { xs: "2rem", md: "3rem" },
        }}
      >
        Welcome to {brand.displayName}!
      </Typography>

      <Typography
        variant="h5"
        sx={{
          fontWeight: 300,
          opacity: 0.9,
          maxWidth: "600px",
          mx: "auto",
          fontSize: { xs: "1.1rem", md: "1.5rem" },
        }}
      >
        Your trusted partner in headache care and management
      </Typography>
    </Box>
  );

  const renderPreQuestionnaire = () => (
    <Fade in timeout={500}>
      <Container maxWidth="md" sx={{ py: { xs: 3, md: 4 } }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 4 }}>
          Let's Get Started
        </Typography>

        <Card
          sx={{
            p: 6,
            borderRadius: 3,
            boxShadow: (theme) => theme.palette.brand.shadow.card,
            textAlign: "center",
          }}
        >
          <QuestionnaireIcon sx={{ fontSize: 64, color: "primary.main", mb: 3 }} />

          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Now let's gather some information about your headaches
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              fontSize: "1.1rem",
              lineHeight: 1.6,
              maxWidth: "500px",
              mx: "auto",
              mb: 4
            }}>
            This information helps us provide you with the most effective care
            and personalized treatment recommendations.
          </Typography>

          <Button
            onClick={handleGoToQuestionnaire}
            color="primary"
            variant="contained"
            disabled={completing || isNavigating}
            size="large"
            sx={{
              px: 6,
              py: 2,
              fontSize: "1.2rem",
              fontWeight: 600,
              borderRadius: 3,
              textTransform: "none",
              boxShadow: (theme) => theme.palette.brand.shadow.card,
              "&:hover": {
                boxShadow: (theme) => theme.palette.brand.shadow.raised,
              },
            }}
          >
            {completing
              ? "Starting..."
              : isNavigating
                ? "Loading Questionnaire..."
                : "Start Questionnaire"}
          </Button>
        </Card>
      </Container>
    </Fade>
  );

  return (
    <Dialog
      open={open}
      fullScreen
      data-testid="welcome-survey-dialog"
      sx={{
        // Theme-derived, not a hardcoded light gradient. This one line was the whole bug:
        // the paper was pinned to #f8fafc/#ffffff while the heading used `text.primary` and
        // the option cards used `background.paper`. In dark mode that painted a near-white
        // heading and dark navy cards onto a white sheet — an invisible title and cards that
        // looked like they belonged to a different page.
        "& .MuiDialog-paper": {
          background: sheetBackground,
        },
      }}
    >
      <DialogContent sx={{ p: 0, overflow: "auto" }}>
        {renderHeroSection()}
        {renderPreQuestionnaire()}
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeSurveyDialog;